import React from 'react'

import { Box } from '@chakra-ui/layout'
import { Button } from '@chakra-ui/button'

import { GoogleMap, MarkerF } from '@react-google-maps/api'

import { useGoogleMap } from '../../hooks/useGoogleMap'
import { useParking } from '../../hooks/useParking'
import { useGeoLocation } from '../../hooks/useGeoLocation'

import { RefreshButton } from './RefreshButton'
import { Marker } from './Marker'

import { ZoomPlusIcon } from '../../assets/icons/ZoomPlusIcon'
import { ZoomMinusIcon } from '../../assets/icons/ZoomMinusIcon'

enum ZoomType {
  DEFAULT,
  PLUS,
  MINUS,
}

export function GoogleMaps() {
  const { zoom, setZoom, activeMarker, selectMarker } = useGoogleMap()
  const { location, userLocation, setLocation } = useGeoLocation()
  const { parkings } = useParking()

  const ref = React.useRef<GoogleMap | null>(null)

  React.useEffect(() => {
    if (activeMarker !== null) {
      ref.current?.state.map?.moveCamera({
        center: activeMarker.position,
        zoom: 16,
      })
    }
  }, [activeMarker])

  function handleChangeZoom(type: ZoomType): void {
    const zoom = ref.current?.state.map?.getZoom()!

    if (type === ZoomType.PLUS) {
      ref.current?.state.map?.setZoom(zoom + 1)
    }

    if (type === ZoomType.MINUS) {
      ref.current?.state.map?.setZoom(zoom - 1)
    }
  }

  return (
    <Box
      paddingTop={{
        base: '20px',
      }}
      padding={{
        notebook: '0px',
      }}
      width={{
        base: '100%',
        notebook: '65%',
      }}
      height="100%"
    >
      <Box as="section" display="block" position="relative" height="100%">
        <RefreshButton />
        <GoogleMap
          id="map"
          ref={ref}
          mapContainerStyle={{
            height: '100%',
            position: 'relative',
            overflow: 'hidden',
          }}
          zoom={zoom}
          center={location}
          options={{
            gestureHandling: 'cooperative',
            zoomControl: false,
            fullscreenControl: false,
            streetViewControl: false,
            mapTypeControl: false,
            minZoom: 13,
            maxZoom: 22,
            styles: [
              {
                featureType: 'poi',
                stylers: [{ visibility: 'off' }],
              },
              {
                featureType: 'transit',
                stylers: [{ visibility: 'off' }],
              },
            ],
          }}
          onClick={() => {
            if (activeMarker !== null) {
              selectMarker(null)
            }
          }}
          onDragEnd={() => {
            const coordinates = ref.current?.state.map?.getCenter()?.toJSON()

            setLocation(coordinates)
          }}
          onZoomChanged={() => setZoom(ref.current?.state.map?.getZoom())}
        >
          <MarkerF
            position={userLocation}
            options={{
              optimized: true,
            }}
          />

          {parkings.map(item => (
            <Marker key={item.id} data={item} />
          ))}
        </GoogleMap>
        <Box
          display={{
            base: 'none',
            notebook: 'flex',
          }}
        >
          <Box
            borderRadius="9999px"
            border="1px solid rgb(215, 215, 224)"
            bottom="24px"
            boxShadow="rgb(152 152 166 / 20%) 0px 7px 8px -4px, rgb(152 152 166 / 14%) 0px 12px 17px 2px, rgb(152 152 166 / 12%) 0px 5px 22px 4px"
            overflow="hidden"
            position="absolute"
            right="24px"
          >
            <Button
              display="flex"
              alignItems="center"
              justifyContent="center"
              fontSize="20px"
              fontWeight="700"
              height="46px"
              width="46px"
              cursor="pointer"
              border="none"
              backgroundColor="rgb(255, 255, 255)"
              borderRadius="0"
              _first={{
                borderBottom: '1px solid rgb(215, 215, 224)',
              }}
              onClick={() => handleChangeZoom(ZoomType.PLUS)}
              isLoading={zoom === 22}
              spinner={<ZoomPlusIcon width="24px" height="24px" />}
            >
              <ZoomPlusIcon width="24px" height="24px" />
            </Button>
            <Button
              display="flex"
              alignItems="center"
              justifyContent="center"
              fontSize="20px"
              height="46px"
              width="46px"
              cursor="pointer"
              border="none"
              backgroundColor="rgb(255, 255, 255)"
              borderRadius="0"
              onClick={() => handleChangeZoom(ZoomType.MINUS)}
              isLoading={zoom === 13}
              spinner={<ZoomMinusIcon width="24px" height="24px" />}
            >
              <ZoomMinusIcon width="24px" height="24px" />
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
